import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import loadable from '@loadable/component';
import clsx from 'clsx';
import Parser, { domToReact } from 'html-react-parser';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import atomOneDark from 'react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-dark-reasonable';
import { Link } from '@tribeplatform/react-ui-kit/Link';
import { MediaModal } from '../MediaModal';
import { ComposerModuleName } from './@types';
import { CodeCopyButton } from './CodeCopyButton';
import { renderAudio, renderImg, renderVideo } from './renderers';
import { imgToImage } from './utils';
const SyntaxHighlighter = loadable(() => import('react-syntax-highlighter'), {
    ssr: false,
});
const parentIsAList = domNode => { var _a; return ['li', 'ol', 'ul'].indexOf((_a = domNode === null || domNode === void 0 ? void 0 : domNode.parent) === null || _a === void 0 ? void 0 : _a.name) > -1; };
export const HtmlContent = ({ value, embeds, mentions, isReply, generateMemberPath, }) => {
    let mediaIndex = -1;
    // The first one is active by default.
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const slides = [];
    const [mediaModalVisibility, setMediaModalVisibility] = useState(false);
    const handleModalVisibility = useCallback(e => {
        var _a;
        setActiveMediaIndex(Number((_a = e === null || e === void 0 ? void 0 : e.currentTarget.dataset) === null || _a === void 0 ? void 0 : _a.index));
        setMediaModalVisibility(true);
    }, []);
    const handleModalClose = useCallback(() => {
        setMediaModalVisibility(false);
    }, []);
    const embedAlignmentClass = isReply ? '' : '-ml-6 -mr-6';
    const options = {
        replace: _domNode => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            const domNode = _domNode;
            const tagName = domNode.name;
            let codeLanguage = 'text';
            switch (tagName) {
                case 'figure': {
                    switch ((_a = domNode.firstChild) === null || _a === void 0 ? void 0 : _a.name) {
                        case 'img': {
                            const imgSlide = imgToImage(domNode.firstChild);
                            if (imgSlide) {
                                slides.push(imgSlide);
                            }
                            mediaIndex += 1;
                            return renderImg(domNode.firstChild, {
                                className: parentIsAList(domNode) ? '' : embedAlignmentClass,
                                mediaIndex,
                                onClick: handleModalVisibility,
                            });
                        }
                        case 'audio':
                            return renderAudio(domNode.firstChild);
                        case 'video':
                            return renderVideo(domNode.firstChild, {
                                className: embedAlignmentClass,
                            });
                        default:
                            return domNode;
                    }
                }
                case 'img': {
                    const imgSlide = imgToImage(domNode);
                    if (imgSlide) {
                        slides.push(imgSlide);
                    }
                    mediaIndex += 1;
                    return renderImg(domNode, {
                        className: parentIsAList(domNode) ? '' : embedAlignmentClass,
                        mediaIndex,
                        onClick: handleModalVisibility,
                    });
                }
                case 'h1':
                case 'h2':
                    return (_jsx("h2", { className: "text-xl", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'audio':
                    return renderAudio(domNode);
                case 'video':
                    return renderVideo(domNode, { className: embedAlignmentClass });
                case 'pre': {
                    if (!((_b = domNode === null || domNode === void 0 ? void 0 : domNode.children) === null || _b === void 0 ? void 0 : _b.length) ||
                        ((_c = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _c === void 0 ? void 0 : _c.name) !== 'code' ||
                        ((_e = (_d = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e.length) !== 1 ||
                        ((_g = (_f = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _f === void 0 ? void 0 : _f.children[0]) === null || _g === void 0 ? void 0 : _g.type) !== 'text')
                        return null;
                    if ((_j = (_h = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _h === void 0 ? void 0 : _h.attribs) === null || _j === void 0 ? void 0 : _j.class) {
                        codeLanguage = (_l = (_k = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _k === void 0 ? void 0 : _k.attribs) === null || _l === void 0 ? void 0 : _l.class.replace('language-', '');
                    }
                    const content = domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children[0].children);
                    return (_jsxs("div", { className: clsx('code-wrapper relative', embedAlignmentClass), children: [_jsx(CodeCopyButton, { content: content }, void 0), _jsx(SyntaxHighlighter, { language: codeLanguage, style: atomOneDark, fallback: _jsx("pre", { children: _jsx("code", { children: content }, void 0) }, void 0), children: content }, void 0)] }, void 0));
                }
                case 'h3':
                    return (_jsx("h2", { className: "text-lg", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'ol':
                    return (_jsx("ol", { start: (_m = domNode === null || domNode === void 0 ? void 0 : domNode.attribs) === null || _m === void 0 ? void 0 : _m.start, className: "list-decimal list-inside pl-6", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'ul':
                    return (_jsx("ul", { className: "list-disc list-inside pl-6", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'div':
                    if (domNode.type === 'tag' &&
                        ((_o = domNode.attribs) === null || _o === void 0 ? void 0 : _o['data-type']) === ComposerModuleName.Embed) {
                        if (!embeds) {
                            return null;
                        }
                        // Fetch the corresponding embed id
                        const embedId = domNode.attribs['data-id'];
                        const embed = embeds.find(embed => embed.id === embedId);
                        const embedRendered = (_jsxs(_Fragment, { children: [_jsx("div", { className: "preview relative w-full", children: _jsx("figure", { dangerouslySetInnerHTML: { __html: embed.html } }, void 0) }, void 0), _jsx(Helmet, { children: _jsx("script", { src: "//cdn.iframe.ly/embed.js", async: true }, void 0) }, void 0)] }, void 0));
                        if ((embed === null || embed === void 0 ? void 0 : embed.type) === 'video')
                            return (_jsx("div", { className: clsx(embedAlignmentClass), children: embedRendered }, void 0));
                        return embedRendered;
                    }
                    break;
                case 'a':
                    if (((_p = domNode.attribs) === null || _p === void 0 ? void 0 : _p['data-type']) === ComposerModuleName.Mention) {
                        const mentionId = domNode.attribs['data-id'];
                        const mentionFromDb = (_q = mentions === null || mentions === void 0 ? void 0 : mentions.find) === null || _q === void 0 ? void 0 : _q.call(mentions, mention => mention.id === mentionId);
                        return (_jsx(Link, { as: RouterLink, to: generateMemberPath({ id: mentionId }), children: (mentionFromDb === null || mentionFromDb === void 0 ? void 0 : mentionFromDb.name)
                                ? `@${mentionFromDb.name}`
                                : domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                    }
                    return (_jsx(Link, { target: "_blank", rel: "noopener noreferrer", href: (_r = domNode === null || domNode === void 0 ? void 0 : domNode.attribs) === null || _r === void 0 ? void 0 : _r.href, children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                default:
                    // If null is returned parser will render the html variant.
                    return null;
            }
            return null;
        },
    };
    const Component = Parser(value, options);
    return (_jsxs(_Fragment, { children: [Component, (slides === null || slides === void 0 ? void 0 : slides.length) > 0 && (_jsx(MediaModal, { slides: slides, isVisible: mediaModalVisibility, onClose: handleModalClose, activeMediaIndex: activeMediaIndex }, void 0))] }, void 0));
};
//# sourceMappingURL=HtmlContent.js.map